/**
 * Where a «Da decidere» row opens — pure, so a test can ask a link what it lands on.
 *
 * **A row's number and the list it opens contain the same rows.** Each count is the decision
 * summary's PENDING count of one queue, so each row opens the page that lists those pending rows:
 * replenishment on the record filtered to `pending`; transfers on the queue that draws every
 * action type's undecided transfers; price steps on the pending-approvals queue, which draws the
 * undecided steps only. Every row opens ON THE RUN it counted (#148 step 1.5a).
 */

import { MOVE_RUN_ID_KEY, NEEDS_ATTENTION_PARAMS, PENDING_PROPOSALS_PARAMS, PENDING_TRANSFERS_PATH } from '@/features/inseason/pending-links';

import type { DecisionId } from './cockpit';
import { RUN_ID_PARAM } from './scope';

/** Where each decision is taken. Paths are the chapters' published routes. */
export const DECISION_TARGET: Readonly<Record<DecisionId, string>> = {
  refusedCandidates: '/inseason/markdown',
  priceSteps: '/inseason/markdown/approvals',
  replenishment: '/inseason/allocation-decisions',
  // Every pending transfer, all action types — not `/inseason/rebalancing`, which decides one purpose at a time.
  transfers: PENDING_TRANSFERS_PATH,
  // Alerts are decided in the workbench's detail pane since ADR 2004 (#148 step 2.4); the
  // foundations inbox reads them and decides nothing.
  openAlerts: '/inseason/workbench',
  writeBack: '/inseason/write-back',
  // «Scale di sconto» stands on Pricing's «Parametri» (ADR 9066); the row opens it on `?app=pricing`.
  ladderExpiry: '/foundations/parameters',
  // The market's process calendar (ADR 9068), opened on the market and year the warning is about.
  pricingCoverage: '/foundations/process-calendar',
};

export interface DecisionLinkContext {
  /** The in-season cycle run on screen. */
  readonly runId: string | null;
  /** The run the price figures were read from (F16), else the cycle run. */
  readonly priceRunId: string | null;
  /** The market and year the pricing-coverage warning reads (ADR 9068), when there is one. */
  readonly pricingCoverage?: { readonly country: string; readonly year: number } | null;
  /** The move run the summary's pending moves are of (platform v0.126.0); the moves page opens on it. */
  readonly transfersMoveRunId?: string | null;
}

export interface DecisionLink {
  readonly path: string;
  readonly params: Readonly<Record<string, string>>;
}

export function decisionLink(id: DecisionId, context: DecisionLinkContext): DecisionLink {
  const path = DECISION_TARGET[id];
  const cycle = context.runId === null ? {} : { [RUN_ID_PARAM]: context.runId };
  const price = context.priceRunId === null ? cycle : { [RUN_ID_PARAM]: context.priceRunId };
  switch (id) {
    case 'refusedCandidates':
    case 'priceSteps':
      return { path, params: price };
    case 'replenishment':
      return { path, params: { ...cycle, ...PENDING_PROPOSALS_PARAMS } };
    case 'transfers': {
      // The one run's merged list, on the move run the count is of — never the cycle run, which names no move run.
      const moveRunId = context.transfersMoveRunId ?? null;
      return { path, params: moveRunId === null ? {} : { [MOVE_RUN_ID_KEY]: moveRunId } };
    }
    case 'openAlerts':
      return { path, params: cycle };
    case 'writeBack':
      // The row counts the commands the summary says need attention, and the log lists those and no others.
      return { path, params: { ...cycle, ...NEEDS_ATTENTION_PARAMS } };
    case 'ladderExpiry':
      // «Scale di sconto» is on Pricing's «Parametri» (ADR 9066), not bound to a run.
      return { path, params: { app: 'pricing' } };
    case 'pricingCoverage': {
      // The calendar of the market and year the warning reads (ADR 9068).
      const coverage = context.pricingCoverage ?? null;
      return { path, params: coverage === null ? {} : { country: coverage.country, year: String(coverage.year) } };
    }
  }
}
